import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrderDetails,
  selectOrderDetails,
  clearOrderDetails,
} from "../../../slice/order/orderSlice";
import api from "../../../api/axios";
import CourierMap from "../component/CourierMap";
import { fetchRatingByOrder, selectOrderRating, clearOrderRating } from "../../../slice/rating/ratingSlice";

/* Every amount on /admin/orders is an integer number of PAISE. */
const rupees = (paise) =>
  ((Number(paise) || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/* ─── Brand palette ─────────────────────────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", honey: "#E8C96A",
  cream: "#FDF6E3", sand: "#F1E6C9", ink: "#15281D", stone: "#4C5C46",
  fail: "#EF5350",
};

/* ─── Status labels (modern + legacy) ───────────────────────── */
const STATUS_LABEL = {
  pending:           "Ordered",
  accepted:          "Accepted",
  assigned:          "Assigned",
  preparing:         "Milling/Packing",
  processing_done:   "Milling/Packing Done",
  waiting_delivery:  "Waiting Delivery",
  ready_pickup:      "Ready for Pickup",
  out_for_delivery:  "Out for Delivery",
  delivered:         "Delivered",
  delivery_failed:   "Delivery Failed",
  left_at_doorstep:  "Left at Doorstep",
  completed:         "Completed",
  could_not_deliver: "Could Not Deliver",
  cancelled:         "Cancelled",
  ready:             "Order Ready",
  out_delivery:      "Out for Delivery",
  out_pickup:        "Out for Pickup",
  shipped:           "Shipped",
};

const humanize = (status) =>
  STATUS_LABEL[status] ||
  String(status || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/* ─── Legacy source states → modern equivalent ──────────────── */
const LEGACY_EQUIV = {
  ready:        "processing_done",
  out_delivery: "out_for_delivery",
  out_pickup:   "ready_pickup",
  shipped:      "out_for_delivery",
};
const modernStatus = (status) => LEGACY_EQUIV[status] || status;

/* ─── Allowed transitions (shared contract) ─────────────────── */
const TRANSITIONS = {
  pending:           ["accepted", "assigned", "cancelled"],
  accepted:          ["assigned", "cancelled"],
  assigned:          ["preparing", "cancelled"],
  preparing:         ["processing_done", "cancelled"],
  processing_done:   ["waiting_delivery", "ready_pickup", "cancelled"],
  waiting_delivery:  ["out_for_delivery", "cancelled"],
  ready_pickup:      ["completed", "cancelled"],
  out_for_delivery:  ["delivered", "delivery_failed", "left_at_doorstep"],
  delivered:         ["completed"],
  left_at_doorstep:  ["completed"],
  delivery_failed:   ["could_not_deliver", "out_for_delivery"],
  could_not_deliver: ["out_for_delivery"],
  completed:         [],
  cancelled:         [],
};

const SUCCESS_STATUSES = ["delivered", "left_at_doorstep", "completed"];
const FAIL_STATUSES    = ["delivery_failed", "could_not_deliver"];

/* gold = active pipeline · pine = success · red = fail · stone = cancelled */
const statusChipStyle = (status) => {
  if (FAIL_STATUSES.includes(status))
    return { bg: "#FDECEA", color: "#C62828", border: BRAND.fail, dot: BRAND.fail };
  if (SUCCESS_STATUSES.includes(status))
    return { bg: "#E4F0EA", color: BRAND.pine, border: "#9CC7B4", dot: BRAND.pine };
  if (status === "cancelled")
    return { bg: "#EFF1ED", color: BRAND.stone, border: "#C9D1C4", dot: BRAND.stone };
  return { bg: BRAND.cream, color: BRAND.gold, border: BRAND.honey, dot: BRAND.gold };
};

/* Button label per target status */
const ACTION_LABEL = {
  accepted:          "Accept Order",
  assigned:          "Mark Assigned",
  preparing:         "Start Milling/Packing",
  processing_done:   "Milling/Packing Done",
  waiting_delivery:  "Waiting for Delivery",
  ready_pickup:      "Ready for Pickup",
  out_for_delivery:  "Out for Delivery",
  delivered:         "Mark Delivered",
  delivery_failed:   "Delivery Failed",
  left_at_doorstep:  "Left at Doorstep",
  completed:         "Complete Order",
  could_not_deliver: "Could Not Deliver",
  cancelled:         "Cancel Order",
};

const FAIL_REASONS = [
  { value: "closed",        label: "Store/Recipient closed" },
  { value: "no_permission", label: "No permission to enter" },
  { value: "other",         label: "Other" },
];

const AVATAR_PALETTE = [
  ["#eff6ff", "var(--milliyo-forest)"], ["#faf5ff", "#7c3aed"],
  ["#f0fdf4", "#16a34a"], ["#fff7ed", "#ea580c"], ["#f0f9ff", "#0284c7"],
];

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata",  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

/* ─── Small reusable card section header ─────────────────────── */
const CardHeader = ({ iconBg, iconStroke, icon, title }) => (
  <div className="card-header" style={{ borderBottom: "1px solid var(--mly-border-soft)", paddingBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9, background: iconBg,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
          fill="none" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {icon}
        </svg>
      </div>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "var(--mly-text)" }}>{title}</h6>
    </div>
  </div>
);

/* ─── InfoRow ─────────────────────────────────────────────────── */
const InfoRow = ({ emoji, value, last }) => (
  <div style={{
    display: "flex", gap: 10, marginBottom: last ? 0 : 10,
    paddingBottom: last ? 0 : 10,
    borderBottom: last ? "none" : "1px solid var(--mly-border-soft)",
    alignItems: "flex-start",
  }}>
    <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{emoji}</span>
    <span style={{ fontSize: 12, color: "var(--mly-text-soft)", lineHeight: 1.6 }}>{value || "—"}</span>
  </div>
);

const renderStars = (value = 0) => (
  <span style={{ fontSize: 18, letterSpacing: 2 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} style={{ color: i < value ? "#f5a623" : "var(--mly-border)" }}>★</span>
    ))}
  </span>
);

/* ─── Split Bill Banner (splitCount > 1) ─────────────────────── */
/* The other legs of the bill arrive as `siblings` on the detail payload,
   already scoped and excluding this order — the old fetch-200-rows-and-
   filter-client-side scan is gone. */
const SplitBillBanner = ({ order, siblings = [] }) => {
  const navigate = useNavigate();
  const billNo = order?.billNo;

  const k = order?.splitIndex || 1;
  const n = order?.splitCount || 1;

  return (
    <div style={{
      background: BRAND.sand, border: `1.5px solid ${BRAND.honey}`,
      borderRadius: 16, padding: "12px 16px", marginBottom: 20,
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 16 }}>📦</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: BRAND.ink }}>
        Delivery {k} of {n} for this bill
      </span>
      {billNo && (
        <span style={{ fontSize: 11, color: BRAND.stone, fontWeight: 600 }}>
          · {billNo}
        </span>
      )}
      {siblings.length > 0 && (
        <span style={{
          display: "flex", alignItems: "center", gap: 6,
          flexWrap: "wrap", marginLeft: "auto",
        }}>
          <span style={{ fontSize: 11, color: BRAND.stone, fontWeight: 600 }}>
            Other deliveries:
          </span>
          {siblings.map((sib) => (
            <button
              key={sib.orderNo}
              onClick={() => navigate(`/admin/orders/details/${sib.orderNo}`)}
              title={`Open ${sib.orderNo}`}
              style={{
                background: "#fff", color: BRAND.pine,
                border: `1px solid ${BRAND.honey}`, borderRadius: 20,
                padding: "3px 10px", fontSize: 11, fontWeight: 700,
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {sib.orderNo}
              {/* A sibling row carries no splitCount — the parent order's n
                  is the same bill's leg count. */}
              {n > 1 ? ` · ${sib.splitIndex || "?"}/${n}` : ""}
            </button>
          ))}
        </span>
      )}
    </div>
  );
};

/* ─── Pipeline Timeline (history stepper) ────────────────────── */
/* `history` is its own array on the detail payload — {status, at, byActor,
   note}, oldest first — not a field on the order. */
const PipelineTimeline = ({ order, history = [] }) => {
  const currentStatus = order?.status || "pending";

  /* If no history, show just the current status as a single step */
  const steps = history.length
    ? history
    : [{ status: currentStatus, at: order?.updatedAt || order?.placedAt, byActor: "", note: "" }];

  return (
    <div className="card" style={{ marginBottom: 0, overflow: "hidden" }}>
      <CardHeader
        iconBg="#f0fdf4" iconStroke="#0f6e56"
        title="Order Pipeline"
        icon={<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>}
      />
      <div className="card-body" style={{ padding: "22px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {steps.map((entry, i) => {
            const isLast    = i === steps.length - 1;
            const isCurrent = entry.status === currentStatus && isLast;
            const sc        = statusChipStyle(entry.status);

            return (
              <div key={`${entry.status}-${i}`} style={{ display: "flex", gap: 14, position: "relative" }}>
                {/* Left line + dot */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20 }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: "50%", flexShrink: 0, marginTop: 3,
                    background: isCurrent ? BRAND.gold : sc.dot,
                    border: `2px solid ${isCurrent ? BRAND.gold : sc.dot}`,
                    boxShadow: isCurrent ? `0 0 0 4px ${BRAND.cream}` : "none",
                    zIndex: 1,
                  }} />
                  {!isLast && (
                    <div style={{
                      width: 2, flex: 1, minHeight: 26,
                      background: "var(--mly-border)",
                      borderRadius: 2, marginTop: 2,
                    }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, paddingBottom: isLast ? 0 : 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      fontWeight: 700, fontSize: 13,
                      color: isCurrent ? BRAND.gold : "var(--mly-text)",
                    }}>
                      {humanize(entry.status)}
                    </span>
                    {isCurrent && (
                      <span style={{
                        background: BRAND.cream, color: BRAND.gold,
                        border: `1px solid ${BRAND.honey}`,
                        fontSize: 10, fontWeight: 700,
                        padding: "2px 8px", borderRadius: 20,
                      }}>
                        Current
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>
                      {formatDateTime(entry.at)}
                    </span>
                    {entry.byActor && (
                      <span style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>· by {entry.byActor}</span>
                    )}
                  </div>
                  {entry.note && (
                    <div style={{
                      fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4,
                      background: "var(--mly-surface-2)", padding: "6px 10px", borderRadius: 8,
                      display: "inline-block",
                    }}>
                      {entry.note}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ─── Courier Live Location Card ─────────────────────────────── */
const CourierLocationCard = ({ order }) => {
  /* Flat on the order now: courierLat/courierLng/courierAt, no loc object.
     null is "never pinged" — Number(null) is 0, which is a real place in
     the Atlantic, so it has to be kept out of the finite check. */
  const coord  = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));
  const courierAt = order?.courierAt || null;
  const lat    = coord(order?.courierLat);
  const lng    = coord(order?.courierLng);
  const hasLoc = Number.isFinite(lat) && Number.isFinite(lng);

  /* Destination pin — shipLat/shipLng are numbers, null when unknown */
  const destLat = coord(order?.shipLat);
  const destLng = coord(order?.shipLng);
  const hasDest = Number.isFinite(destLat) && Number.isFinite(destLng);

  /* Freshness */
  let minutesAgo = null;
  if (hasLoc && courierAt) {
    const ms = Date.now() - new Date(courierAt).getTime();
    if (Number.isFinite(ms)) minutesAgo = Math.max(0, Math.floor(ms / 60000));
  }
  const isStale = minutesAgo !== null && minutesAgo > 3;

  const gmapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
  const routeUrl = hasLoc && hasDest
    ? `https://www.google.com/maps/dir/?api=1&origin=${lat},${lng}&destination=${destLat},${destLng}`
    : null;

  const pillStyle = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 14px", borderRadius: 10,
    fontSize: 12, fontWeight: 700, textDecoration: "none",
  };

  return (
    <div className="card" style={{ marginBottom: 0, overflow: "hidden" }}>
      <CardHeader
        iconBg={BRAND.cream} iconStroke={BRAND.gold}
        title="Courier Live Location"
        icon={<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>}
      />
      <div className="card-body" style={{ padding: "18px 20px" }}>
        {!hasLoc ? (
          <div style={{
            background: "var(--mly-surface-2)", border: `1.5px dashed ${BRAND.sand}`,
            borderRadius: 8, padding: "36px 16px",
            textAlign: "center", color: "var(--mly-text-muted)",
            fontSize: 13, fontWeight: 600,
          }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>📍</div>
            Waiting for the delivery partner's location…
          </div>
        ) : (
          <>
            {/* Mr MilliyO glides to each new fix — the marker is moved, not
                rebuilt, so the map never flashes on a courier update */}
            <CourierMap
              lat={lat}
              lng={lng}
              destLat={destLat}
              destLng={destLng}
              stale={isStale}
            />

            {/* Freshness line */}
            <div style={{
              display: "flex", alignItems: "center", gap: 8, marginTop: 12,
              padding: "8px 12px", borderRadius: 8,
              background: isStale ? "#FDECEA" : "#E4F0EA",
              border: `1px solid ${isStale ? BRAND.fail : "#9CC7B4"}`,
              fontSize: 12, fontWeight: 600,
              color: isStale ? "#C62828" : BRAND.pine,
              flexWrap: "wrap",
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                background: isStale ? BRAND.fail : BRAND.pine,
              }} />
              <span>
                {minutesAgo === null
                  ? "Live position"
                  : minutesAgo < 1
                    ? "Updated just now"
                    : `Updated ${minutesAgo} min ago`}
                {isStale && " · Location may be stale"}
              </span>
              {courierAt && (
                <span style={{
                  marginLeft: "auto", fontSize: 11, fontWeight: 500,
                  color: isStale ? "#C62828" : BRAND.stone,
                }}>
                  {formatDateTime(courierAt)}
                </span>
              )}
            </div>

            {/* Map links */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <a
                href={gmapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...pillStyle, background: BRAND.pine, color: "#fff" }}
              >
                Open in Google Maps
              </a>
              {routeUrl && (
                <a
                  href={routeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...pillStyle, background: BRAND.cream, color: BRAND.gold,
                    border: `1.5px solid ${BRAND.honey}`,
                  }}
                >
                  Route to customer
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── Assignment Card ─────────────────────────────────────────── */
const AssignmentCard = ({ order, onAssigned }) => {
  const [millers,        setMillers]        = useState([]);
  const [deliveryStaff,  setDeliveryStaff]  = useState([]);
  const [selMiller,      setSelMiller]      = useState("");
  const [selDelivery,    setSelDelivery]    = useState("");
  const [assigning,      setAssigning]      = useState(false);
  const [assignError,    setAssignError]    = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        /* GET /admin/staff always answers {data:[...]} and deliberately
           includes deactivated rows — `isActive`, not `active`. */
        const [mRes, dRes] = await Promise.all([
          api.get("/admin/staff?role=miller_packer"),
          api.get("/admin/staff?role=delivery"),
        ]);
        const normalize = (res) =>
          (res.data?.data || []).filter((s) => s.isActive !== false);
        setMillers(normalize(mRes));
        setDeliveryStaff(normalize(dRes));
      } catch (err) {
        setAssignError(err.response?.data?.message || "Failed to load staff list");
      }
    };
    load();
  }, []);

  /* Preselect current assignment. The order carries only millerName /
     deliveryName — the staff id is not returned — so the selection is
     matched by name against the loaded list. Keyed on the resolved ids so
     the 10s live refresh, which replaces the order object every poll,
     cannot clobber an in-progress selection. */
  const idByName = (list, name) =>
    (name && list.find((s) => s.name === name)?.id) || "";
  const assignedMillerId   = idByName(millers, order?.millerName);
  const assignedDeliveryId = idByName(deliveryStaff, order?.deliveryName);
  useEffect(() => { setSelMiller(String(assignedMillerId || "")); },     [assignedMillerId]);
  useEffect(() => { setSelDelivery(String(assignedDeliveryId || "")); }, [assignedDeliveryId]);

  /* POST /fulfilment/orders/{orderNo}/assign takes exactly ONE staffId and
     derives the column from that staff row's role, so a miller and a rider
     are two sequential calls. There is no unassign: staffId is required and
     must resolve to an active staff row, so "— Not assigned —" cannot be
     submitted (see the disabled Assign button below).
     TODO(cutover): no server endpoint for clearing an assignment. */
  const handleAssign = async () => {
    setAssignError("");
    setAssigning(true);
    try {
      const targets = [selMiller, selDelivery].filter(Boolean);
      for (const staffId of targets) {
        await api.post(`/fulfilment/orders/${order.orderNo}/assign`, {
          staffId: Number(staffId),
        });
      }
      onAssigned();
    } catch (err) {
      const code = err.response?.status;
      const data = err.response?.data;
      setAssignError(
        code === 404 && data?.code === "unknown_staff"
          ? "That staff member is unknown or deactivated."
        : code === 404 ? "Order not found."
        : code === 409 ? "That staff member belongs to another store."
        : code === 403 ? "You cannot assign staff to this order."
        : data?.message || "Failed to assign staff"
      );
    } finally {
      setAssigning(false);
    }
  };

  const selectStyle = {
    width: "100%", padding: "9px 12px", borderRadius: 10,
    border: "1.5px solid var(--mly-border)", fontSize: 13,
    outline: "none", color: "var(--mly-text)", background: "var(--mly-surface)", cursor: "pointer",
  };

  const labelStyle = {
    display: "block", fontSize: 10, fontWeight: 700, letterSpacing: 1,
    color: "var(--mly-text-muted)", marginBottom: 5, textTransform: "uppercase",
  };

  return (
    <div className="card padding" style={{ marginBottom: 0 }}>
      <CardHeader
        iconBg={BRAND.cream} iconStroke={BRAND.gold}
        title="Assignment"
        icon={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>}
      />
      <div className="card-body mt-3">
        {assignError && (
          <div style={{
            background: "#fef2f2", color: "#dc2626",
            padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12,
          }}>{assignError}</div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Miller / Packer</label>
          <select value={selMiller} onChange={(e) => setSelMiller(e.target.value)} style={selectStyle}
            onFocus={(e) => (e.target.style.borderColor = BRAND.pine)}
            onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}>
            <option value="">— Not assigned —</option>
            {millers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {order?.millerName && (
            <div style={{ fontSize: 11, color: "var(--mly-heading)", marginTop: 4, fontWeight: 600 }}>
              Currently: {order.millerName}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Delivery Staff</label>
          <select value={selDelivery} onChange={(e) => setSelDelivery(e.target.value)} style={selectStyle}
            onFocus={(e) => (e.target.style.borderColor = BRAND.pine)}
            onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}>
            <option value="">— Not assigned —</option>
            {deliveryStaff.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {order?.deliveryName && (
            <div style={{ fontSize: 11, color: "var(--mly-heading)", marginTop: 4, fontWeight: 600 }}>
              Currently: {order.deliveryName}
            </div>
          )}
        </div>

        <button
          onClick={handleAssign}
          disabled={assigning || (!selMiller && !selDelivery)}
          style={{
            width: "100%", padding: "10px 0", borderRadius: 10, border: "none",
            background: assigning || (!selMiller && !selDelivery) ? "#9CC7B4" : BRAND.pine,
            color: "#fff", fontWeight: 700, fontSize: 13,
            cursor: assigning || (!selMiller && !selDelivery) ? "not-allowed" : "pointer",
          }}
        >
          {assigning ? "Assigning…" : "Assign"}
        </button>
      </div>
    </div>
  );
};

/* ─── Actions Card ───────────────────────────────────────────── */
const ActionsCard = ({ order, onChanged }) => {
  const [acting,       setActing]       = useState(false);
  const [actionError,  setActionError]  = useState("");
  const [showFailForm, setShowFailForm] = useState(false);
  const [failReason,   setFailReason]   = useState("closed");
  const [failOther,    setFailOther]    = useState("");

  const current    = modernStatus(order?.status);
  const isPickup   = order?.fulfilment === "pickup";
  let   nextStates = TRANSITIONS[current] || [];

  /* Filter branch targets by delivery mode (both stay allowed per contract) */
  nextStates = nextStates.filter((s) => {
    if (isPickup  && (s === "waiting_delivery")) return false;
    if (!isPickup && (s === "ready_pickup"))     return false;
    return true;
  });

  /* Delivery legs are rider-app-only (server enforces with a 403): the
     portal prepares orders and completes STORE PICKUPS, nothing else. */
  const RIDER_ONLY = [
    "out_for_delivery", "delivered", "delivery_failed", "left_at_doorstep",
  ];
  const riderHandlesNext =
    !isPickup && nextStates.some((s) => RIDER_ONLY.includes(s));
  if (!isPickup) {
    nextStates = nextStates.filter((s) => !RIDER_ONLY.includes(s));
  }

  const doStatus = async (status, extra = {}) => {
    setActionError("");
    if (status === "cancelled" &&
        !window.confirm("Cancel this order? Held stock will be released and the customer notified.")) {
      return;
    }
    setActing(true);
    try {
      /* The reply is {orderNo, from, to} — not the order — so the page has
         to refetch; nothing can be merged back into state from it. The
         local TRANSITIONS map only filters the buttons: shop.advance_order
         is the authority and answers 409 illegal_transition when it
         disagrees, with a message that names both states. */
      await api.post(`/fulfilment/orders/${order.orderNo}/status`, { status, ...extra });
      setShowFailForm(false);
      setFailOther("");
      onChanged();
    } catch (err) {
      const code = err.response?.status;
      const msg  = err.response?.data?.message;
      setActionError(
        code === 409 ? (msg || "That status change is not allowed from here.")
        : code === 404 ? "Order not found."
        : code === 403 ? "This order belongs to another store."
        : msg || "Failed to update status"
      );
    } finally {
      setActing(false);
    }
  };

  const submitFail = () => {
    const reasonText = failReason === "other"
      ? (failOther.trim() || "Other")
      : (FAIL_REASONS.find((r) => r.value === failReason)?.label || failReason);
    /* `reason` (was failReason) is only persisted on delivery_failed, where
       it lands in delivery_fail_reason and reads back as deliveryFailReason. */
    doStatus("delivery_failed", { reason: reasonText });
  };

  const actionLabel = (target) => {
    if (target === "out_for_delivery" &&
        (current === "delivery_failed" || current === "could_not_deliver")) {
      return "Retry Delivery";
    }
    return ACTION_LABEL[target] || humanize(target);
  };

  const buttonStyleFor = (target) => {
    if (target === "cancelled") return {
      background: "#fff1f1", color: "#dc2626", border: "1.5px solid #fca5a5",
    };
    if (FAIL_STATUSES.includes(target)) return {
      background: "#FDECEA", color: "#C62828", border: `1.5px solid ${BRAND.fail}`,
    };
    if (SUCCESS_STATUSES.includes(target)) return {
      background: BRAND.pine, color: "#fff", border: "1.5px solid transparent",
    };
    return {
      background: BRAND.gold, color: "#fff", border: "1.5px solid transparent",
    };
  };

  return (
    <div className="card padding" style={{ marginBottom: 0 }}>
      <CardHeader
        iconBg="#e1f5ee" iconStroke={BRAND.pine}
        title="Actions"
        icon={<><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>}
      />
      <div className="card-body mt-3">
        {actionError && (
          <div style={{
            background: "#fef2f2", color: "#dc2626",
            padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12,
          }}>{actionError}</div>
        )}

        {riderHandlesNext && (
          <div style={{
            background: "var(--mly-surface-3)", color: "var(--mly-text)",
            padding: "10px 12px", borderRadius: 10, fontSize: 12,
            marginBottom: nextStates.length ? 12 : 0, fontWeight: 600,
          }}>
            🛵 From here the assigned rider takes over in the MilliyO Rider
            app (pickup, delivered, failed, left at door). The portal only
            completes store pickups.
          </div>
        )}
        {nextStates.length === 0 ? (
          riderHandlesNext ? null : (
          <div style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
            No further actions — this order is {humanize(order?.status).toLowerCase()}.
          </div>
          )
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {nextStates.map((target) => {
              const st = buttonStyleFor(target);
              if (target === "delivery_failed") {
                return (
                  <div key={target}>
                    <button
                      onClick={() => setShowFailForm((v) => !v)}
                      disabled={acting}
                      style={{
                        width: "100%", padding: "10px 0", borderRadius: 10,
                        ...st,
                        fontWeight: 700, fontSize: 13,
                        cursor: acting ? "not-allowed" : "pointer",
                      }}
                    >
                      {actionLabel(target)}
                    </button>
                    {showFailForm && (
                      <div style={{
                        marginTop: 8, padding: "12px 12px", borderRadius: 10,
                        background: "#FDECEA", border: `1px solid ${BRAND.fail}`,
                        display: "flex", flexDirection: "column", gap: 8,
                      }}>
                        <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#C62828", textTransform: "uppercase" }}>
                          Failure Reason
                        </label>
                        <select
                          value={failReason}
                          onChange={(e) => setFailReason(e.target.value)}
                          style={{
                            width: "100%", padding: "8px 10px", borderRadius: 8,
                            border: "1.5px solid var(--mly-border)", fontSize: 13,
                            outline: "none", background: "var(--mly-surface)", cursor: "pointer",
                          }}
                        >
                          {FAIL_REASONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                        {failReason === "other" && (
                          <input
                            type="text"
                            value={failOther}
                            onChange={(e) => setFailOther(e.target.value)}
                            placeholder="Describe the reason…"
                            style={{
                              width: "100%", padding: "8px 10px", borderRadius: 8,
                              border: "1.5px solid var(--mly-border)", fontSize: 13,
                              outline: "none", background: "var(--mly-surface)", boxSizing: "border-box",
                            }}
                          />
                        )}
                        <button
                          onClick={submitFail}
                          disabled={acting}
                          style={{
                            padding: "9px 0", borderRadius: 8, border: "none",
                            background: acting ? "#f3b0ae" : BRAND.fail,
                            color: "#fff", fontWeight: 700, fontSize: 13,
                            cursor: acting ? "not-allowed" : "pointer",
                          }}
                        >
                          {acting ? "Saving…" : "Confirm Delivery Failed"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={target}
                  onClick={() => doStatus(target)}
                  disabled={acting}
                  style={{
                    width: "100%", padding: "10px 0", borderRadius: 10,
                    ...st,
                    fontWeight: 700, fontSize: 13,
                    cursor: acting ? "not-allowed" : "pointer",
                    opacity: acting ? 0.7 : 1,
                  }}
                >
                  {actionLabel(target)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────── */
/* The order's tax invoice and any credit notes, as PDFs. Fetched as a blob
   because the route needs the admin token, which a plain link cannot carry. */
const TaxDocumentLinks = ({ documents }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  if (!documents.length) return null;

  const download = async (d) => {
    setBusy(d.id);
    setError("");
    try {
      const res = await api.get(`/admin/documents/${d.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${d.docNo}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      setError("Could not download that document.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 6 }}>
        Tax documents
      </div>
      {documents.map((d) => (
        <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
          <div style={{ fontSize: 13, color: "var(--mly-text)" }}>
            <strong>{d.kind === "credit_note" ? "Credit note" : "Invoice"}</strong> {d.docNo}
            <span style={{ color: "var(--mly-text-muted)" }}> · ₹{rupees(d.totalPaise)}</span>
          </div>
          <button type="button" onClick={() => download(d)} disabled={busy === d.id} style={{
            background: "transparent", color: BRAND.pine, border: `1.5px solid ${BRAND.pine}`, borderRadius: 8,
            padding: "4px 10px", fontSize: 12, fontWeight: 700, cursor: busy === d.id ? "default" : "pointer",
          }}>
            {busy === d.id ? "…" : "PDF"}
          </button>
        </div>
      ))}
      {error && <div style={{ fontSize: 12, color: "#C62828" }}>{error}</div>}
    </div>
  );
};

/* Cash on delivery reaches the till: the rider (or the counter) took it at
   handover, and this press moves it out of the COD receivable the invoice
   booked. The service answers 0 on a second press, never twice the cash. */
const CodCashRow = ({ order, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (order.paymentMethod !== "cod" || !SUCCESS_STATUSES.includes(modernStatus(order.status))) return null;

  if (order.codCollectedAt) {
    return (
      <div style={{ gridColumn: "1 / -1", fontSize: 12, color: "#0f6e56", fontWeight: 600 }}>
        Cash received {formatDateTime(order.codCollectedAt)}
      </div>
    );
  }

  const collect = async () => {
    if (!window.confirm(`Confirm ${rupees(order.totalPaise)} in cash has reached the till?`)) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/fulfilment/orders/${order.orderNo}/cod-collected`);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || "Could not record the cash.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <button type="button" onClick={collect} disabled={busy} style={{
        background: BRAND.pine, color: "#fff", border: "none", borderRadius: 8,
        padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer",
      }}>
        {busy ? "Recording…" : "Cash received"}
      </button>
      {error && <div style={{ marginTop: 6, fontSize: 12, color: "#C62828" }}>{error}</div>}
    </div>
  );
};

const ViewOrder = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const detail    = useSelector(selectOrderDetails);
  const loading   = useSelector((s) => s.orders.loading);
  const error     = useSelector((s) => s.orders.error);

  const orderRating = useSelector(selectOrderRating);

  /* GET /admin/orders/{orderNo} answers five siblings at the top level —
     {order, customer, items, history, siblings} — not one flat order. The
     route matches order_no OR legacy_id, so an old Mongo _id in the URL
     still resolves; every WRITE below addresses the order by order.orderNo,
     which is the only thing /fulfilment matches.

     TODO(cutover): slice/order/orderSlice.js was NOT cut over — its
     fetchOrderDetails still calls GET /api/orders/{id} AND unwraps
     res.data.data, so this page renders "Order not found" on the .NET
     service. It needs GET /admin/orders/{orderNo} returning res.data (the
     body IS the payload). Everything below already reads the new shape. */
  const order    = detail?.order || null;
  const customer = detail?.customer || null;
  const items    = detail?.items || [];
  const history  = detail?.history || [];
  const siblings = detail?.siblings || [];
  const documents = detail?.documents || [];

  useEffect(() => {
    dispatch(fetchOrderDetails(id));
    // TODO(cutover): no server endpoint for this yet — the only ratings route
    // is POST /customer/ratings (write); there is no admin read by order, so
    // the Customer Rating card below has no source.
    dispatch(fetchRatingByOrder(id));
    return () => {
      dispatch(clearOrderDetails());
      dispatch(clearOrderRating());
    };
  }, [dispatch, id]);

  const refreshOrder = () => dispatch(fetchOrderDetails(id));

  /* Live refresh — re-fetch the order every 10s so the timeline, status
     and courier location update without a manual refresh (this replaces
     the old courier-only 15s poll; it also covers it). Background
     refetches never flash the spinner — the page only blocks on !order. */
  const isOutForDelivery = modernStatus(order?.status) === "out_for_delivery";

  /* A finished order does not change again, and a tab nobody is looking at
     does not need to be told. Polling both anyway meant every completed order
     left open in a tab asked the server six times a minute, for ever, for an
     answer that could not differ. */
  const settled = ["delivered", "completed", "cancelled", "could_not_deliver", "returned"]
    .includes(modernStatus(order?.status));
  useEffect(() => {
    if (settled) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      dispatch(fetchOrderDetails(id));
    }, 10000);
    return () => clearInterval(timer);
  }, [dispatch, id, settled]);

  /* Only block the page when there is no order yet — background refetches
     (15s live-location polling) must not flash the loading spinner. */
  if (!order) {
    return (
      <div className="container-fluid" style={{ paddingTop: 60, textAlign: "center", color: "var(--mly-text-muted)" }}>
        {loading ? (
          <>
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
            Loading order…
          </>
        ) : (
          <span style={{ color: "#ef4444" }}>{error || "Order not found."}</span>
        )}
      </div>
    );
  }

  const sc          = statusChipStyle(order.status);
  const orderNo     = order.orderNo || "";
  const avatarIdx   = orderNo ? orderNo.charCodeAt(orderNo.length - 1) % AVATAR_PALETTE.length : 0;
  const avatarStyle = AVATAR_PALETTE[avatarIdx];
  /* The customer record, not the shipping snapshot — a pickup has no snapshot. */
  const custName    = customer?.name  || order.shipName  || "";
  const custEmail   = customer?.email || order.shipEmail || "";
  const custPhone   = customer?.phone || order.shipPhone || "";

  return (
    <div>
      <div className="container-fluid" style={{ marginBottom: "30px", padding: "5px" }}>

        {/* ── Top Bar ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 22, flexWrap: "wrap", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: "15px" }}>
            <button
              onClick={() => navigate("/admin/order/recevied")}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
                borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                fontSize: 13, fontWeight: 600, color: "var(--mly-text-soft)",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--mly-surface-3)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "var(--mly-surface-2)"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M10.6 19.4a1.2 1.2 0 0 1-1.7 0l-6.4-6.4a1.2 1.2 0 0 1 0-1.7l6.4-6.4a1.2 1.2 0 1 1 1.7 1.7L6.3 10.8H20a1.2 1.2 0 0 1 0 2.4H6.3l4.3 4.3a1.2 1.2 0 0 1 0 1.9Z" /></svg>
              Back
            </button>
            <div>
              <span style={{ fontWeight: 800, fontSize: 19, color: "var(--mly-text)", letterSpacing: "-0.3px" }}>
                {order.orderNo}
              </span>
              <span style={{ marginLeft: 10, fontSize: 12, color: "var(--mly-text-muted)" }}>
                {formatDateTime(order.placedAt)}
              </span>
            </div>
          </div>

          {/* Status + payment badges */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{
              background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`,
              borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot, display: "inline-block" }} />
              {humanize(order.status)}
            </span>
            {order.retryRequested && (
              <span style={{
                background: BRAND.cream, color: BRAND.gold, border: `1.5px solid ${BRAND.honey}`,
                borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
              }}>
                Retry requested by customer
              </span>
            )}
            <span style={{
              background: order.paymentStatus === "paid" ? "#e1f5ee" : "#faeeda",
              color:      order.paymentStatus === "paid" ? "#0f6e56" : "#854f0b",
              border:     `1.5px solid ${order.paymentStatus === "paid" ? "#9fe1cb" : "#fac775"}`,
              borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
            }}>
             {
  order.paymentMethod?.toUpperCase() === "RAZORPAY"
    ? "Online Payment"
    : order.paymentMethod?.toUpperCase() || "—"
}
{" · "}
{order.paymentStatus || "—"}
            </span>
            {order.trackingUrl && (
              <a
                href={order.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: "#f0f9ff", color: "#0284c7", border: "1.5px solid #bae6fd",
                  borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
                  textDecoration: "none", display: "flex", alignItems: "center", gap: 5,
                }}
              >
                Track Shipment
              </a>
            )}
          </div>
        </div>

        {/* ── Split-bill banner (only when this bill has N deliveries) ── */}
        {(order.splitCount || 1) > 1 && <SplitBillBanner order={order} siblings={siblings} />}

        {/* ── Main Grid ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }}>

          {/* ════ LEFT COLUMN ════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* ── Pipeline Timeline ── */}
            <PipelineTimeline order={order} history={history} />

            {/* ── Courier Live Location (only while out for delivery) ── */}
            {isOutForDelivery && <CourierLocationCard order={order} />}

            {/* Order Items */}
            <div className="card" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#eff6ff" iconStroke="var(--milliyo-forest)"
                title="Order Items"
                icon={<><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></>}
              />
              <div className="card-body" style={{ padding: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "var(--mly-surface-2)" }}>
                      {["Product", "Name", "Qty", "Unit Price", "Total"].map((h, i) => (
                        <th key={h} style={{
                          padding: "10px 16px",
                          textAlign: i === 0 ? "left" : i === 4 ? "right" : "center",
                          fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)",
                          textTransform: "uppercase", letterSpacing: "0.05em",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} style={{ borderTop: "1px solid var(--mly-border-soft)" }}>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                              width: 42, height: 42, borderRadius: 10,
                              background: "var(--mly-surface-3)", overflow: "hidden",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}>
                              {/* `image` is ONE full URL, not a filename to prefix.
                                  Checkout does not populate image_urls today, so
                                  expect null and the placeholder below. */}
                              {item.image ? (
                                <img alt={item.name} src={item.image}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                  fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21 15 16 10 5 21"/>
                                </svg>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: "var(--mly-text)" }}>
                              {item.name || "—"}
                            </span>
                            {item.millLabel && (
                              <span style={{
                                background: BRAND.cream, color: BRAND.gold,
                                border: `1px solid ${BRAND.honey}`,
                                fontSize: 10, fontWeight: 700,
                                padding: "2px 8px", borderRadius: 20,
                                whiteSpace: "nowrap",
                              }}>
                                {item.millLabel}
                              </span>
                            )}
                          </div>
                          {/* TODO(cutover): server does not return the per-line
                              `texts` array — no column, so the line is dropped. */}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "center", fontWeight: 700, fontSize: 14, color: "var(--mly-text)" }}>
                          {item.quantity}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "center", fontSize: 13, color: "var(--mly-text-muted)" }}>
                          ₹{rupees(item.unitPaise)}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: 700, fontSize: 14, color: "var(--mly-text)" }}>
                          ₹{rupees(item.totalPaise)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Price summary */}
                <div style={{ borderTop: "2px solid var(--mly-border-soft)", padding: "16px 20px" }}>
                  <div style={{ maxWidth: 280, marginLeft: "auto" }}>
                    {[
                      { label: "Subtotal", value: order.subtotalPaise || 0 },
                      { label: "Discount", value: order.discountPaise || 0, minus: true },
                      { label: "Shipping", value: order.shippingPaise || 0 },
                    ].map((row) => (
                      <div key={row.label} style={{
                        display: "flex", justifyContent: "space-between",
                        marginBottom: 7, fontSize: 13, color: "var(--mly-text-muted)",
                      }}>
                        <span>{row.label}</span>
                        <span style={{ color: row.minus ? "#ef4444" : undefined }}>
                          {row.minus ? "−" : ""}₹{rupees(Math.abs(row.value))}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      display: "flex", justifyContent: "space-between",
                      borderTop: "1.5px solid var(--mly-border)", paddingTop: 11, marginTop: 6,
                      fontWeight: 800, fontSize: 15, color: "var(--mly-text)",
                    }}>
                      <span>Order Total</span>
                      <span style={{ color: "var(--mly-heading)" }}>₹{rupees(order.totalPaise)}</span>
                    </div>
                    {/* GST is inside the tax-inclusive prices — snapshot from
                        order time; absent on orders placed before tracking.
                        Halved in paise so the two halves add back to the tax
                        actually charged on an odd total. */}
                    {order.taxPaise > 0 && (
                      <div style={{
                        textAlign: "right", marginTop: 6,
                        fontSize: 11.5, color: "var(--mly-text-muted)",
                      }}>
                        Includes GST ₹{rupees(order.taxPaise)}
                        {" "}(CGST ₹{rupees(Math.floor(order.taxPaise / 2))}
                        {" + "}SGST ₹{rupees(order.taxPaise - Math.floor(order.taxPaise / 2))})
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* ════ RIGHT COLUMN ════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* ── Assignment Card ── */}
            <AssignmentCard order={order} onAssigned={refreshOrder} />

            {/* ── Actions Card ── */}
            <ActionsCard order={order} onChanged={refreshOrder} />

            {/* Customer Card */}
            <div className="card padding" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#fff7ed" iconStroke="#ea580c"
                title="Customer"
                icon={<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>}
              />
              <div className="card-body mt-3">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <div style={{
                    width: 50, height: 50, borderRadius: 13,
                    background: avatarStyle[0], color: avatarStyle[1],
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 16, letterSpacing: 1,
                    border: `2px solid ${avatarStyle[1]}30`, flexShrink: 0,
                  }}>
                    {getInitials(custName)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "var(--mly-text)" }}>{custName || "—"}</div>
                    <div style={{ fontSize: 12, color: "var(--mly-text-muted)", marginTop: 1 }}>Customer</div>
                  </div>
                </div>
                <InfoRow emoji="✉️" value={custEmail} />
                <InfoRow emoji="📞" value={custPhone} />
                {/* TODO(cutover): server does not return state or pincode on the
                    shipping snapshot — the address line loses those two parts. */}
                <InfoRow emoji="📍" value={[order.shipAddress, order.shipLandmark, order.shipDistrict].filter(Boolean).join(", ")} last />

                {/* ── Delivery flags near the address ── */}
                {(order.leaveAtDoor || order.deliveryFailReason || order.retryRequested) && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
                    {order.leaveAtDoor && (
                      <div style={{
                        background: BRAND.cream, color: BRAND.gold,
                        border: `1.5px solid ${BRAND.honey}`,
                        borderRadius: 10, padding: "8px 12px",
                        fontSize: 12, fontWeight: 700,
                        display: "flex", alignItems: "center", gap: 8,
                      }}>
                        <span style={{ fontSize: 15 }}>🚪</span> Leave at door requested
                      </div>
                    )}
                    {order.deliveryFailReason && (
                      <div style={{
                        background: "#FDECEA", color: "#C62828",
                        border: `1.5px solid ${BRAND.fail}`,
                        borderRadius: 10, padding: "8px 12px",
                        fontSize: 12, fontWeight: 600,
                      }}>
                        <span style={{ fontWeight: 700 }}>Last delivery failed:</span>{" "}
                        {order.deliveryFailReason}
                      </div>
                    )}
                    {order.retryRequested && (
                      <div style={{
                        background: "#E4F0EA", color: BRAND.pine,
                        border: "1.5px solid #9CC7B4",
                        borderRadius: 10, padding: "8px 12px",
                        fontSize: 12, fontWeight: 700,
                        display: "flex", alignItems: "center", gap: 8,
                      }}>
                        🔁 Retry requested by customer
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Card */}
            <div className="card padding" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#f0fdf4" iconStroke="#16a34a"
                title="Payment"
                icon={<><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></>}
              />
              <div className="card-body mt-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  {
  label: "Method",
  value:
    order.paymentMethod?.toUpperCase() === "RAZORPAY"
      ? "Online Payment"
      : order.paymentMethod?.toUpperCase() || "—",
},
                  { label: "Status",  value: order.paymentStatus || "—",
                    badge: { bg: order.paymentStatus === "paid" ? "#e1f5ee" : "#faeeda",
                             color: order.paymentStatus === "paid" ? "#0f6e56" : "#854f0b" } },
                  /* razorpayOrderId is paymentOrderRef now */
                  { label: "Order ID", value: order.paymentOrderRef || "—", full: true },
                ].map((item) => (
                  <div key={item.label} style={{ gridColumn: item.full ? "1 / -1" : undefined }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                      {item.label}
                    </div>
                    {item.badge ? (
                      <span style={{
                        background: item.badge.bg, color: item.badge.color,
                        fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                      }}>
                        {item.value}
                      </span>
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)", wordBreak: "break-all" }}>
                        {item.value}
                      </div>
                    )}
                  </div>
                ))}
                <CodCashRow order={order} onChanged={refreshOrder} />
                <TaxDocumentLinks documents={documents} />
              </div>
            </div>

            {/* ── Estimated Delivery Card ── */}
            {/* Read-only: estimatedDeliveryAt/estimatedMinutes come back on the
                detail read, but nothing in the service ever writes them —
                see the TODO in OrderRecevied's status handlers. */}
            {order.estimatedDeliveryAt && (
              <div className="card padding" style={{ marginBottom: 0 }}>
                <CardHeader
                  iconBg="#faf5ff" iconStroke="#7c3aed"
                  title="Estimated Delivery"
                  icon={<><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>}
                />
                <div className="card-body mt-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                      Date
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)" }}>
                      {formatDate(order.estimatedDeliveryAt)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                      Time
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)" }}>
                      {new Date(order.estimatedDeliveryAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata",
                        hour: "2-digit", minute: "2-digit", hour12: true,
                      })}
                    </div>
                  </div>
                  {order.estimatedMinutes && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                        Duration
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)" }}>
                        {order.estimatedMinutes} min
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors */}
            {error && (
              <div style={{
                background: "#fef2f2", color: "#dc2626",
                padding: "8px 12px", borderRadius: 8, fontSize: 12,
              }}>{error}</div>
            )}


{/* ── Customer Rating Card ── */}
{orderRating && (
  <div className="card padding" style={{ marginBottom: 0 }}>
    <CardHeader
      iconBg="#fff7ed" iconStroke="#f59e0b"
      title="Customer Rating"
      icon={<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>}
    />
    <div className="card-body mt-3">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: orderRating.review ? 10 : 0 }}>
        {renderStars(orderRating.value)}
        <span style={{ fontWeight: 700, fontSize: 13, color: "var(--mly-text)" }}>
          {orderRating.value}/5
        </span>
      </div>
      {orderRating.review && (
        <div style={{ fontSize: 12, color: "var(--mly-text-muted)", lineHeight: 1.6, background: "var(--mly-surface-2)", padding: "8px 10px", borderRadius: 8 }}>
          "{orderRating.review}"
        </div>
      )}
      <div style={{ fontSize: 10, color: "var(--mly-text-muted)", marginTop: 8 }}>
        Rated on {formatDate(orderRating.createdAt)}
      </div>
    </div>
  </div>
)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewOrder;
